import { OrderStatusTypeEnum } from 'app/shared/enums/order-status-type.enum.model';
import { ChannelDTO } from 'app/shared/models/channel.model';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { OrderItem } from 'app/shared/models/order-item.model';
import { OrderValidationResponseModel } from 'app/shared/models/order-validation.model';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';

import { DiscountApplicationTypeEnum } from '../../../shared/enums/discount-application-type.enum.model';
import { MediaTypeEnum } from '../../../shared/enums/media-type.enum.model';
import { SizeGroupTypeEnum } from '../enum/size-group-type.enum.model';
import { DuplicateCartAgencies } from '../models/back-office-admin-common/duplicate-cart.models';
import { MediaDTO } from '../../../shared/models/media.models';
import { NewOrderXlsxVm } from '../models/back-office-admin-common/new-order.models';
import { OrderItemsInsertDTO } from '../models/back-office-admin-common/order-items-insert.models';
import { OrderProductListVM } from '../models/back-office-admin-common/order-product-list.model';
import { OrderDeliveryDate, OrderDTO, OrderVM } from '../models/back-office-admin-common/order.model';
import { AddOrEditVariantVM as AddOrEditOrderItemVM } from '../models/back-office-admin-common/variant.models';
import { PointOfSaleDetailVM } from '../models/point-of-sale/bo-point-of-sale.model';
import { AppliedManualDiscountTypeEnum } from '../models/back-office-admin-common/applied-manual-discount.model';
import { OrderTypeEnum } from 'app/shared/enums/order-type.enum.model';
import { GenderTypeEnum } from '../enum/gender-type.enum.model';

export class OrderMapper {
  public static orderItems(orderItem: OrderItem, orderId: number, orderValidation: OrderValidationResponseModel, channel: ChannelDTO, orderType: OrderTypeEnum, isConfirmedOrder?: boolean): OrderProductListVM {
    const sizeGroupType = SizeGroupTypeEnum;
    const quantityMinimumPerOrder = orderValidation?.minimumQuantityValidationByProductPalette?.find(x => x.productId === orderItem.variant?.productId && x.paletteId === orderItem.variant?.palette?.id)?.minimumQuantityPerOrder || orderItem.variant.quantityMinimumPerOrder || 1;
    const orderItemAvailableQTY = orderValidation?.orderItemsValidation?.find(x => x.orderItemId === orderItem.id)?.availableQuantity;
    const realQty = isConfirmedOrder ? (orderItem.deliveries.reduce((c, d) => c + (d.erpQuantity - d.erpCancelledQuantity), 0) + (orderItem.alreadyFulfilledQuantity ?? 0)) : orderItem.quantity;
    const myDiscountedUnitPrice = isConfirmedOrder ? orderItem.erpDiscountedUnitPrice : orderItem.discountedUnitPrice;
    const marginValue = myDiscountedUnitPrice - orderItem.variant.cost;
    const marginPercentage = Number(((marginValue / myDiscountedUnitPrice) * 100).toFixed(2));
    const commrecialDisc = orderItem.appliedManualDiscounts?.find(x => x.discountType === AppliedManualDiscountTypeEnum.commercial);
    const financialDisc = orderItem.appliedManualDiscounts?.find(x => x.discountType === AppliedManualDiscountTypeEnum.financial);
    const result: OrderProductListVM = {
      ...orderItem,
      sizeGroupId: orderItem.variant.size.group,
      gender: GenderTypeEnum[orderItem.variant.gender],
      parentId: orderItem.variant.productId + ' - ' + orderItem.variant.palette.id,
      productId: orderItem.variant.productId,
      imgPath: UtilityFunctions.fixImagePath(orderItem.variant.media[0]?.src),
      colorDetails: orderItem.variant.palette ? orderItem.variant.palette.externalColorCode + ' - ' + orderItem.variant.palette.description : orderItem.variant.palette.description,
      productDetails: orderItem.variant.productCode + ' - ' + orderItem.variant.productTitle,
      productCode: orderItem.variant.productCode,
      // GET /orders/{id} manda il codice esterno come `productExtCode`; extCode resta come fallback.
      extCode: orderItem.variant.productExtCode ?? orderItem.variant.extCode,
      extPaletteId: orderItem.variant.extPaletteId,
      productTitle: orderItem.variant.productTitle,
      colorName: orderItem.variant.palette.description,
      colorCode: orderItem.variant.palette.externalColorCode,
      ean: orderItem.variant.ean,
      orderId,
      orderItemId: orderItem.id,
      marginValue: orderItem.variant.cost ? marginValue : 0,
      marginTotValue: orderItem.variant.cost ? marginValue * realQty : 0,
      marginPercentage: orderItem.variant.cost ? marginPercentage : 0,
      orderItemDeliveryDate: isConfirmedOrder ? orderItem.deliveries.sort((d1, d2) => {
        if (d1.erpDeliveryDate < d2.erpDeliveryDate) return -1;
        if (d1.erpDeliveryDate > d2.erpDeliveryDate) return 1;
      }).find(x => x).erpDeliveryDate : orderItem.requestedDeliveryDate,
      requestedDeliveryDate: orderItem.requestedDeliveryDate,
      size: orderItem.variant.size?.name,
      sizeGroup: sizeGroupType[orderItem.variant.size?.group],
      sizeOrder: orderItem.variant.size?.sizeOrder,
      sku: orderItem.variant.code,
      discountedUnitPrice: myDiscountedUnitPrice,
      totDiscountValue: (orderItem.unitPriceWholesale - myDiscountedUnitPrice) * realQty,
      totalRetail: isConfirmedOrder ? orderItem.erpTotalRetailPrice : orderItem.unitPriceRetail * orderItem.quantity,
      totalWholesale: orderItem.unitPriceWholesale * realQty,
      wholeSalePrice: orderItem.unitPriceWholesale,
      automaticDiscountByValue: this.OrderAutomaticDiscount(orderItem, DiscountApplicationTypeEnum.ByValue),
      automaticDiscountByQTY: this.OrderAutomaticDiscount(orderItem, DiscountApplicationTypeEnum.ByQuantity),
      channelDiscountPerc: orderItem.storeChannelDiscount?.discount,
      itemCost: orderItem.variant.cost,
      costTotValue: orderItem.variant.cost ? orderItem.variant.cost * orderItem.quantity : null,
      discountedTotalPrice: Number((myDiscountedUnitPrice * realQty).toFixed(2)),
      availableQuantity: UtilityFunctions.defineAvailableQty(orderItemAvailableQTY, quantityMinimumPerOrder, channel, orderType),
      totalDiscountPercentage: +((orderItem.unitPriceWholesale - myDiscountedUnitPrice) * 100 / orderItem.unitPriceWholesale).toFixed(2),
      totalDiscountPercentageFromRetail: +((orderItem.unitPriceRetail - myDiscountedUnitPrice) * 100 / orderItem.unitPriceRetail).toFixed(2),
      realQuantity: realQty,
      quantityBuyIncrement: orderItem.variant.quantityBuyIncrement,
      quantityMinimumPerOrder: quantityMinimumPerOrder,
      // Le righe salvate a prezzo target non hanno una percentuale digitata: il BE la ricava dagli snapshot
      // e la restituisce a piena precisione (es. 34.5455). Qui è un valore da mostrare, quindi si arrotonda.
      commercialDiscountPerc: UtilityFunctions.roundToTwoDecimals(commrecialDisc?.discountPercentage),
      // Base esatta dallo snapshot: ricavarla dalla percentuale arrotondata sopra sfaserebbe il prezzo di qualche centesimo.
      commercialDiscountBasePrice: commrecialDisc?.discountedUnitPriceBeforeDiscount || myDiscountedUnitPrice,
      commercialDiscountTotValue: commrecialDisc ? (commrecialDisc.discountedUnitPriceBeforeDiscount - commrecialDisc.discountedUnitPriceAfterDiscount) * realQty : 0,
      financialDiscountPerc: financialDisc?.discountPercentage,
      financialDiscountTotValue: financialDisc ? (financialDisc.discountedUnitPriceBeforeDiscount - financialDisc.discountedUnitPriceAfterDiscount) * realQty : 0,
      autoDiscountTotValue: orderItem.appliedAutomaticDiscounts?.length ? orderItem.appliedAutomaticDiscounts.reduce((sum, el) => sum + el.deductedUnitPriceAmount * realQty, 0) : 0,
      agreementDiscountTotValue: (orderItem.agreementDiscountDeductedUnitPriceAmount || 0) * realQty,
      storechannelDiscountTotValue: (orderItem.storeChannelDiscountDeductedUnitPriceAmount || 0) * realQty,
      productStyleGroup: orderItem.variant.productStyleGroup,
      erpCancelledQuantity: isConfirmedOrder ? orderItem.deliveries.reduce((c, d) => c + d.erpCancelledQuantity, 0) : 0,
      erpQuantity: isConfirmedOrder ? orderItem.deliveries.reduce((c, d) => c + d.erpQuantity, 0) : 0,
      cancellationReason: orderItem?.cancellationReason,
    };
    return result;
  }

  private static OrderAutomaticDiscount(orderItem: OrderItem, applicationType: DiscountApplicationTypeEnum) {
    return orderItem.appliedAutomaticDiscounts.find(x => x.applicationType === applicationType)?.discountPercentage;
  }

  public static OrderVm(order: OrderDTO): OrderVM {
    const pos = order.pointOfSale;
    const isConfirmed = order.orderStatus === OrderStatusTypeEnum.Confirmed || order.orderStatus >= OrderStatusTypeEnum.Partially_fulfilled;
    const isConfirmedPartiallyFulfilled = order.orderStatus === OrderStatusTypeEnum.Confirmed || order.orderStatus === OrderStatusTypeEnum.Partially_fulfilled;
    const totalWholeSale = isConfirmed ? order.orderItems.reduce((a, b) => a + (b.deliveries.reduce((c, d) => c + (d.erpQuantity - d.erpCancelledQuantity), 0)) * b.unitPriceWholesale, 0) : order.totalWholesalePrice;
    const totalNet = isConfirmed ? order.orderItems.reduce((a, b) => a + b.erpDiscountedTotalPrice, 0) : order.discountedTotalPrice;
    let realNet = totalNet - (order.appliedVouchers?.reduce((sum, p) => sum + p.usedAmount, 0) || 0);
    if (realNet < 0) realNet = 0;
    const result: OrderVM = {
      ...order,
      totalDiscountPerc: +((totalWholeSale - realNet) * 100 / totalWholeSale).toFixed(2),
      totalWholeSale: totalWholeSale,
      discountedTotalPrice: order.orderStatus > OrderStatusTypeEnum.Created ? order.erpDiscountedTotalPrice : order.discountedTotalPrice,
      statusDetail: order.statusDetail?.replace(/\\r|\"/g, "")?.replace(/\\n/g, "\n")?.replace(/([^n])\\+/g, " ") ?? '',
      channelName: order.channel.name,
      customerName: !!pos?.customer ? pos.customer.name : '',
      posName: pos ? (pos.name + (pos.location.address1 ? ' - ' + pos.location.address1 : '') + (pos.location.city ? ', ' + pos.location.city : '')) : '',
      orderQty: order.orderItems.reduce((a, b) => a + b.quantity, 0),
      totalRetailPrice: order.orderItems.reduce((a, b) => a + (b.unitPriceRetail * b.quantity), 0),
      channelSupportStockLimit: order.channel.supportsStockLimits,
      isConfirmed,
      checked: false,
      isEditable: isConfirmedPartiallyFulfilled
    };
    return result;
  }

  public static AddOrEditVariantVM(orderItem: OrderItem, orderProductList: OrderProductListVM) {
    const result: AddOrEditOrderItemVM = {
      deliveries: orderItem.deliveries,
      variantId: orderItem.variantId,
      orderQuantity: orderItem.quantity,
      mediaImgSrc: orderItem.variant.media[0]?.src,
      palette: orderItem.variant.palette,
      size: orderItem.variant.size,
      availableQuantity: orderProductList.availableQuantity,
      wholesalePrice: orderProductList.wholeSalePrice,
      note: orderProductList.note,
      internalNote: orderProductList.internalNote,
      requestedDeliveryDate: orderProductList.orderItemDeliveryDate,
      orderItemId: orderProductList.orderItemId,
      quantityBuyIncrement: orderItem.variant.quantityBuyIncrement ?? 1,
      quantityMinimumPerOrder: orderItem.variant.quantityMinimumPerOrder ?? 1,
    };

    return result;
  }

  public static NewOrderXlsxVm(pos: PointOfSaleDetailVM, customerId?: number, billingLocationId?: number, storeId?: number, agencyId?: number) {
    const result: NewOrderXlsxVm = {
      name: '',
      agencyId: agencyId,
      internalNote: 'Created by XLSX',
      agenciesData: new DuplicateCartAgencies(),
      pointOfSaleId: pos.id,
      pointOfSaleName: pos.name,
      shippingLocationId: pos.location.id,
      checked: false,
      posLocation: pos.location,
      fileXlsx: null,
      billingLocationId: billingLocationId ?? null,
      customer: customerId ?? null,
      note: '',
      storeId: storeId ?? null,
      type: null,
    };

    return result;
  }

  public static AssociateOrderItems(order: OrderDTO[], orderItems: OrderItem[] | OrderItem[], baseUrl: string) {
    const result: HttpBasicRequest[] = [];
    order.forEach(order => {
      orderItems.forEach(element => {
        const body: OrderItemsInsertDTO = {
          variantId: element.variantId,
          quantity: element.quantity,
          note: element.note,
          internalNote: element.internalNote,
          requestedDeliveryDate: element.requestedDeliveryDate,
        };
        result.push({ url: baseUrl + `/orders/${order.id}/order-items`, method: 'post', body });
      });
    });
    return result;
  }

  public static MediaUrl(media: MediaDTO[]) {
    let result = '';
    let rawUrl = media.find(element => element.type === MediaTypeEnum.image)?.src;
    if (rawUrl?.indexOf('https://') < 0) {
      result = 'https://' + rawUrl;
    }
    return result;
  }

  public static sendEmailsRequest(emails: string[], subject: string, message: string, baseUrl: string,): HttpBasicRequest[] {
    const sendEmailsRequest: HttpBasicRequest[] = [];
    emails.forEach(email => {
      sendEmailsRequest.push({ url: baseUrl + `/email/send-mail`, method: 'post', body: { email, subject, message } });
    });
    return sendEmailsRequest;
  }

  public static OrderDeliveryDate(orderItems: OrderItem[], orderProductList: OrderProductListVM[]): OrderDeliveryDate[] {
    const elaboratedOrderItems = UtilityFunctions.elaborateOrderItemsByDates(orderItems, orderProductList);
    return UtilityFunctions.deliveryDates(elaboratedOrderItems.map(x => { return { data: x.children.dataset, orderItemDeliveryDate: x.orderItemDeliveryDate, hasCustomDeliveryDate: x.hasCustomDeliveryDate, quantity: x.realQuantity } }));
  }
}
